import { Validator, Success, Failure, ValidationError } from "@totalsoft/pure-validations";
import { isValidPhoneNumber, parsePhoneNumber } from "react-phone-number-input";
import moment from "moment";

import i18next from "i18next";

const defaultMessages = {
  "Validations.Generic.Phone": "The value is not a valid phone number"
};

function translate(key, args) {
  return i18next.t(key, { defaultValue: defaultMessages[key], ...args }) || defaultMessages[key];
}

export const testPhone = (phone, countryCode) => {
  let phoneNumber = parsePhoneNumber(phone);
  if (!phoneNumber) phoneNumber = parsePhoneNumber(phone, countryCode);

  return isValidPhoneNumber((phoneNumber && phoneNumber.number) || phone);
};

export const validatePersonUniqueId = (uniqueId, countryCode) => {
  switch (countryCode) {
    case "RO":
      return isCNP(uniqueId);
    case "BG":
      return isEGN(uniqueId);
    default:
      return true;
  }
};

var isCIF = function isCIF(cif) {
  if (!cif) return true;

  if (cif.toUpperCase().includes("RO")) {
    cif = cif.toUpperCase().replace("RO", "");
  }

  cif = cif.trim();

  if (cif.length < 2 || cif.length > 10) {
    return false;
  }

  if (isNaN(Number(cif)) || parseInt(cif, 10) !== Number(cif)) {
    return false;
  }

  var controlNo = cif.substr(-1);
  cif = cif.substring(0, cif.length - 1);

  while (cif.length !== 9) {
    cif = "0" + cif;
  }

  var sum =
    cif[0] * 7 + cif[1] * 5 + cif[2] * 3 + cif[3] * 2 + cif[4] * 1 + cif[5] * 7 + cif[6] * 5 + cif[7] * 3 + cif[8] * 2;
  sum = sum * 10;
  var rest = sum % 11;

  if (rest === 10) {
    rest = 0;
  }

  if (rest === parseInt(controlNo, 10)) return true;
  else return false;
};

var isUIC = function isUIC(uic) {
  if (!uic) return false;
  const re = /^BG\d{9}$/;

  return re.test(uic);
};

export const validateCompanyUniqueId = (uniqueId, countryCode) => {
  switch (countryCode) {
    case "RO":
      return isCIF(uniqueId);
    case "BG":
      return isUIC(uniqueId);
    default:
      return true;
  }
};

var isEGN = function isEGN(p_egn) {
  if (!p_egn) return true;
  p_egn = p_egn.trim();

  if (p_egn.length !== 10) {
    return false;
  }

  if (isNaN(Number(p_egn)) || parseInt(p_egn, 10) !== Number(p_egn)) {
    return false;
  }

  var year = 0,
    month = 0,
    day = 0,
    hashResult = 0,
    egn = [],
    hashTable = [2, 4, 8, 5, 10, 9, 7, 3, 6];

  for (var i = 0; i < 10; i++) {
    egn[i] = parseInt(p_egn[i], 10);

    if (i < 9) {
      hashResult += egn[i] * hashTable[i];
    }
  }

  hashResult = hashResult % 11;
  if (hashResult === 10) {
    hashResult = 0;
  }

  year = egn[0] * 10 + egn[1];
  month = egn[2] * 10 + egn[3];
  day = egn[4] * 10 + egn[5];

  if (month >= 1 && month <= 12) {
    year += 1900;
  } else if (month >= 21 && month <= 32) {
    month -= 20;
    year += 1800;
  } else if (month >= 41 && month <= 52) {
    month -= 40;
    year += 2000;
  } else {
    return false;
  }

  if (year < 1800 || year > 2099) {
    return false;
  }

  var date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() + 1 !== month || date.getDate() !== day) {
    return false;
  }

  return egn[9] === hashResult;
};

var isCNP = function isCNP(p_cnp) {
  if (!p_cnp) return true;
  p_cnp = p_cnp.trim();

  if (p_cnp.length !== 13) {
    return false;
  }

  if (isNaN(Number(p_cnp)) || parseInt(p_cnp, 10) !== Number(p_cnp)) {
    return false;
  }

  var i = 0,
    year = 0,
    month = 0,
    day = 0,
    hashResult = 0,
    cnp = [],
    hashTable = [2, 7, 9, 1, 4, 6, 3, 5, 8, 2, 7, 9];

  for (i = 0; i < 13; i++) {
    cnp[i] = parseInt(p_cnp[i], 10);

    if (i < 12) {
      hashResult = hashResult + cnp[i] * hashTable[i];
    }
  }

  hashResult = hashResult % 11;

  if (hashResult === 10) {
    hashResult = 1;
  }

  year = cnp[1] * 10 + cnp[2];
  month = cnp[3] * 10 + cnp[4];
  day = cnp[5] * 10 + cnp[6];

  switch (cnp[0]) {
    case 1:
    case 2:
      year += 1900;
      break;

    case 3:
    case 4:
      year += 1800;
      break;

    case 5:
    case 6:
      year += 2000;
      break;

    case 7:
    case 8:
    case 9:
      year += 2000;

      if (year > new Date().getFullYear() - 18) {
        year -= 100;
      }

      break;

    default: {
      return false;
    }
  }

  if (year < 1800 || year > 2099) {
    return false;
  }
  
  if (!(0, moment)("".concat(year, "-").concat(month, "-").concat(day)).isValid()) {
    return false;
  }

  return cnp[12] === hashResult;
};

export const isPhoneByCountry = countryCode => {
  return Validator(function isPhoneByCountry(value) {
    return !value || testPhone(value, countryCode)
      ? Success
      : Failure(ValidationError(translate("Validations.Generic.Phone")));
  });
};

export const phone = Validator(function phone(x) {
  const re =
    /^((07[0-9]{1}[0-9]{1}){1}?(\s|\.)?([0-9]{3}(\s|\.|)){2})|0090\d{9,10}$|(\+)90\d{9,10}$|0033\d{9,10}$|(\+)33\d{9,10}$|00373\d{9,10}$|(\+)373\d{9,10}$|0039\d{9,10}$|(\+)39\d{9,10}$|0049\d{9,10}$|(\+)49\d{9,10}$/;
  return re.test(String(x).toLowerCase()) ? Success : Failure(ValidationError(translate("Validations.Generic.Phone")));
});

export const dateIsLessThanOrEqualToCurrentDate = Validator(function dateIsLessThanOrEqualToCurrentDate(x) {
  if (x) {
    return moment(x).isSameOrBefore(new moment().format("YYYY-MM-DD"))
      ? Success
      : Failure(ValidationError(i18next.t("Validations.Generic.DateLessThanOrEqualToCurrentDate")));
  }
  return Success;
});

export const dateIsGreaterThanGivenDate = (date, givenDateName) =>
  Validator(function dateIsGreaterThanGivenDate(x) {
    if (date && x) {
      return moment(x).isAfter(new Date(date), "day")
        ? Success
        : Failure(ValidationError(i18next.t("Validations.Generic.DateGreaterThanGivenDate", { givenDateName })));
    }
    return Success;
  });

export const validateDistanceBetweenTwoDates = (startDate, startDateName, endDateName, days) =>
  Validator(function validateDistanceBetweenTwoDates(endDate) {
    if (startDate && endDate) {
      var a = moment(startDate, "YYYY-MM-DD");
      var b = moment(endDate, "YYYY-MM-DD");
      var period = b.diff(a, "days");

      return period >= days
        ? Success
        : Failure(
            ValidationError(
              i18next.t("Validations.Generic.DiffBetweenTwoDates", {
                startDateName,
                endDateName,
                days
              })
            )
          );
    }
    return Success;
  });
